package dev.lingvo.site.pages.app.projects.slug

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.ProjectDetailDto
import dev.lingvo.shared.Role
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.components.ProjectIcon
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LEmptyStateIcon
import dev.lingvo.site.ds.LModal
import dev.lingvo.site.ds.LTab
import dev.lingvo.site.ds.LTabs
import dev.lingvo.site.ds.LToastVariant
import dev.lingvo.site.ds.LoadingState
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.ToastHost
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.ds.rememberToasts
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.nav.AppDestination
import dev.lingvo.site.nav.AppNav
import dev.lingvo.site.pages.app.projects.slug.settings.ProjectDangerZone
import dev.lingvo.site.pages.app.projects.slug.settings.ProjectFileSettings
import dev.lingvo.site.pages.app.projects.slug.settings.ProjectIdentitySettings
import dev.lingvo.site.pages.app.projects.slug.settings.ProjectLanguageSettings
import dev.lingvo.site.pages.app.projects.slug.settings.ProjectProviderSettings
import dev.lingvo.site.pages.app.projects.slug.settings.ProjectQaRulesSettings
import dev.lingvo.site.pages.app.projects.slug.settings.ProjectSettingsSurface
import dev.lingvo.site.pages.app.projects.slug.settings.WorkspacePlanUsage
import kotlin.coroutines.resume
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.suspendCancellableCoroutine
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.Element
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.EventListener
import org.w3c.dom.events.MouseEvent

@Page("/app/projects/{slug}/settings")
@Layout(".components.layouts.AppLayout")
@Composable
fun ProjectSettingsPage() {
    val ctx = rememberPageContext()
    val slug = ctx.route.params["slug"].orEmpty()
    var refreshKey by remember { mutableStateOf(0) }
    // A token, not a key. Saving the project reloads it, and re-keying `loadAsync` on the counter
    // discarded the whole page first: the tabs vanished, a five-row skeleton took their place, and
    // the reader's own tab selection came back as "general". The loaded settings stay on screen and
    // only their contents change.
    val reloadable = loadReloadable(slug, reloadToken = refreshKey) { ApiClient.getProject(slug) }
    val toasts = rememberToasts()
    // The mark identifies the project these settings belong to, and it is only knowable once the
    // project has loaded. Rendering the header outside the async content keeps the page's identity
    // on screen while it loads and adds the mark when there is one, rather than blanking the H1.
    val loadedProject = (reloadable.state as? Async.Success)?.value

    ProjectSettingsSurface {
        Column(Modifier.fillMaxWidth().gap(Space.XXL)) {
            PageHeader(
                title = t("app.projects.slug.settings.project_settings", "Project settings"),
                // **No subtitle.** It was the tab strip rewritten as prose — "Project identity,
                // languages, files, workspace plan and translation providers." names the same
                // destinations the strip 40px below it lists, and lists them in a sentence a reader
                // cannot press. Spec §2.1: navigation is chrome, and content must not restate it.
                mark = loadedProject?.let { project ->
                    {
                        ProjectIcon(
                            slug = project.slug,
                            name = project.name,
                            iconKey = project.iconKey,
                            accentKey = project.accentKey,
                            hasCustomIcon = project.hasCustomIcon,
                            modifier = Modifier.width(40.px).height(40.px),
                        )
                    }
                },
                meta = loadedProject?.let { project ->
                    {
                        Span(Modifier.toAttrs { attr("dir", "auto") }) { Text(project.name) }
                        // Labelled, and in mono. As a bare span the slug sat in the same face and
                        // colour as the name a `Space.LG` gap to its left, so "Lingvo Mobile   demo"
                        // read as a second, lower-case project name rather than as the identifier
                        // the CLI and the API take. `docs/DESIGN.md` sets identifiers in mono
                        // because keys are code; the label and the value are one fact, so they sit
                        // `Space.XS` apart inside the meta row's `Space.LG` rhythm.
                        Row(Modifier.gap(Space.XS), verticalAlignment = Alignment.CenterVertically) {
                            Span { Text(t("app.projects.slug.settings.slug", "Slug")) }
                            Span(Modifier.monoFont().color(ColorMode.current.toSitePalette().textPrimary).toAttrs()) {
                                Text(project.slug)
                            }
                        }
                    }
                },
            )
            reloadable.refreshError?.let { failure ->
                LAlert(
                    t("app.projects.slug.settings.showing_the_last_settings_that_loaded", "Showing the last settings that loaded"),
                    failure.readable(),
                    modifier = Modifier.fillMaxWidth(),
                    variant = LAlertVariant.WARNING,
                    live = true,
                )
            }
            LAsyncContent(reloadable.state, rows = 5, onRetry = { refreshKey++ }) { project ->
                LoadedSettingsTabs(
                    project = project,
                    slug = slug,
                    onChanged = { refreshKey++ },
                    onDeleted = { ctx.router.tryRoutingTo("/app") },
                    notify = toasts::show,
                )
            }
        }
    }
    ToastHost(toasts)
}

internal fun projectSettingsRefusal(role: Role?): String? = when {
    role == null -> null
    // "Workspace admins" drops the owners, who hold this too. Every other refusal in the product
    // names the pair.
    AppNav.projectSettingsFor(role).isEmpty() -> tr(
            "app.projects.slug.settings.managed_by_owner_or_admin",
            "Project settings are managed by an owner or admin.",
        )
    else -> null
}

@Composable
private fun LoadedSettingsTabs(
    project: ProjectDetailDto,
    slug: String,
    onChanged: () -> Unit,
    onDeleted: () -> Unit,
    notify: (String, LToastVariant, Long) -> Int,
) {
    val ctx = rememberPageContext()
    val role = Session.roleInOrg(project.orgId)
    val visibleTabs = AppNav.projectSettingsFor(role)
    val initialTab = remember(role) {
        val requested = window.location.search.removePrefix("?").split('&')
            .firstOrNull { it.substringBefore('=') == "tab" }
            ?.substringAfter('=')
        AppNav.resolveProjectTab(requested, role)
    }
    var selected by remember(initialTab) { mutableStateOf(initialTab.orEmpty()) }
    val selectTab: (String) -> Unit = { tab ->
        selected = tab
        runCatching {
            val query = if (tab == "general") "" else "?tab=$tab"
            window.history.replaceState(null, "", "${window.location.pathname}$query")
        }
    }
    val orgSlug = project.orgSlug.ifBlank { Session.slugForOrg(project.orgId).orEmpty() }
    val tabContent: @Composable (String) -> Unit = { destinationId ->
        when (destinationId) {
            "general" -> ProjectIdentitySettings(project, slug, { onChanged() }, notify)
            "languages" -> ProjectLanguageSettings(project, slug, onChanged, notify)
            "files" -> ProjectFileSettings(project, slug, onChanged, notify)
            "plan" -> WorkspacePlanUsage(
                orgSlug = orgSlug,
                role = role,
                onManagePlan = { ctx.router.tryRoutingTo("/app/settings/billing") },
            )
            "providers" -> ProjectProviderSettings(slug = slug, notify = notify)
            // **Written before the destination exists.** "QA rules" joins `PROJECT_SETTINGS` in
            // `AppNavigation.kt`, which this wave's shell task owns; `SettingsTabs` renders whatever
            // that list contains, so this branch is inert until the destination lands and correct
            // the moment it does. `/app/projects/{slug}/settings/qa` keeps the same surface
            // reachable for the roles the tab list will never offer it to.
            // The languages come from the project this page already loaded rather than from the
            // route cache, so the language-exception picker is right on the first frame.
            "qa" -> ProjectQaRulesSettings(
                slug = slug,
                targetLocales = project.locales - project.sourceLocale,
            )
            "danger" -> ProjectDangerZone(project, slug, role, onDeleted, notify)
        }
    }
    val refusal = projectSettingsRefusal(role)
    when {
        refusal != null -> LEmptyState(
            title = t("app.projects.slug.settings.admin_access_required", "Admin access required"),
            description = refusal,
            modifier = Modifier.fillMaxWidth(),
            icon = LEmptyStateIcon.FORBIDDEN,
        ) {
            LButton(
                label = t("app.projects.slug.settings.back_to_project_overview", "Back to project overview"),
                onClick = { ctx.router.tryRoutingTo("/app/projects/$slug") },
                variant = LButtonVariant.SECONDARY,
            )
        }
        visibleTabs.isEmpty() -> LoadingState(rows = 3, label = t("app.projects.slug.settings.loading_settings", "Loading settings"))
        else -> SettingsTabs(visibleTabs, selected, selectTab, tabContent)
    }
}

@Composable
internal fun SettingsTabs(
    destinations: List<AppDestination>,
    selectedId: String,
    onSelected: (String) -> Unit,
    content: @Composable (String) -> Unit,
) {
    val tabs = destinations.map { destination ->
        LTab(destination.id, destination.label) { content(destination.id) }
    }
    LTabs(
        tabs,
        selectedId,
        onSelected,
        Modifier.fillMaxWidth(),
        // The tab list's accessible name. It was a bare English literal, so a translated settings
        // page announced its own navigation in English.
        tr("app.projects.slug.settings.project_settings_tabs", "Project settings"),
    )
}

/**
 * Protects a mounted settings draft from browser unload, in-app links, and tab changes.
 *
 * The native unload prompt is the only browser-controlled boundary. In-app navigation stays inside
 * the design system so cancel can restore focus and discard can reset the draft before replaying the
 * exact action the user requested.
 */
@Composable
internal fun SettingsDraftProtection(
    dirty: Boolean,
    guardId: String,
    onDiscard: () -> Unit,
) {
    var pendingTarget by remember { mutableStateOf<HTMLElement?>(null) }
    val bypass = remember { mutableStateOf(false) }

    DisposableEffect(dirty) {
        if (!dirty) return@DisposableEffect onDispose { }

        val beforeUnload = EventListener { event ->
            if (bypass.value) return@EventListener
            event.preventDefault()
            event.asDynamic().returnValue = ""
        }
        val navigationGuard = EventListener { rawEvent ->
            if (bypass.value) return@EventListener
            val event = rawEvent as? MouseEvent ?: return@EventListener
            if (event.button.toInt() != 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
                return@EventListener
            }
            val target = event.target.nearestSettingsNavigationTarget() ?: return@EventListener
            if (target is HTMLAnchorElement) {
                if (target.hasAttribute("download") || target.target == "_blank") return@EventListener
                if (target.href == window.location.href || target.href.startsWith("javascript:")) return@EventListener
            } else if (target.getAttribute("aria-selected") == "true") {
                return@EventListener
            }
            event.preventDefault()
            event.stopImmediatePropagation()
            pendingTarget = target
        }

        window.addEventListener("beforeunload", beforeUnload)
        document.addEventListener("click", navigationGuard, true)
        onDispose {
            window.removeEventListener("beforeunload", beforeUnload)
            document.removeEventListener("click", navigationGuard, true)
        }
    }

    DiscardSettingsDraftModal(
        open = pendingTarget != null,
        guardId = guardId,
        onCancel = { pendingTarget = null },
        onDiscard = {
            val target = pendingTarget
            if (target != null) {
                bypass.value = true
                onDiscard()
                pendingTarget = null
                window.requestAnimationFrame {
                    target.click()
                    window.setTimeout({ bypass.value = false }, 1_000)
                }
            }
        },
    )
}

/** Frames the discard dialog will wait for its safe action to mount before giving up. */
private const val FOCUS_SETTLE_FRAMES = 20

@Composable
internal fun DiscardSettingsDraftModal(
    open: Boolean,
    guardId: String,
    onCancel: () -> Unit,
    onDiscard: () -> Unit,
) {
    val cancelId = "$guardId-draft-keep-editing"
    // **Wait for the button, do not assume one frame produced it.**
    //
    // This dialog is the last thing standing between somebody and losing what they typed, and the
    // safe action has to be the one under their hands. The modal mounts through a presence
    // transition, so after a single animation frame the button often does not exist yet; focusing
    // "whatever is there" then silently leaves focus on the control that triggered the guard, and a
    // keyboard user has to hunt for the dialog they did not ask for. Poll a bounded number of
    // frames instead — it settles on the first frame in the common case and still gives a loaded
    // machine time to mount.
    LaunchedEffect(open, cancelId) {
        if (!open) return@LaunchedEffect
        repeat(FOCUS_SETTLE_FRAMES) {
            suspendCancellableCoroutine<Unit> { continuation ->
                window.requestAnimationFrame {
                    if (continuation.isActive) continuation.resume(Unit)
                }
            }
            val cancel = document.getElementById(cancelId) as? HTMLElement
            if (cancel != null) {
                cancel.focus()
                return@LaunchedEffect
            }
        }
    }

    LModal(
        open = open,
        title = t("app.projects.slug.settings.discard_unsaved_changes", "Discard unsaved changes?"),
        onClose = onCancel,
        actions = {
            LButton(
                label = t("app.projects.slug.settings.keep_editing", "Keep editing"),
                onClick = onCancel,
                modifier = Modifier.attr("id", cancelId),
                variant = LButtonVariant.GHOST,
            )
            LButton(
                label = t("app.projects.slug.settings.discard_changes", "Discard changes"),
                onClick = onDiscard,
                variant = LButtonVariant.DANGER,
            )
        },
    ) {
        LAlert(
            title = t("app.projects.slug.settings.changes_havent_been_saved", "Changes haven't been saved"),
            description = t("app.projects.slug.settings.discard_this_draft_and_continue_to", "Discard this draft and continue to the page you selected?"),
            variant = LAlertVariant.WARNING,
        )
    }
}

private fun Any?.nearestSettingsNavigationTarget(): HTMLElement? {
    var element = this as? Element
    while (element != null) {
        if (element is HTMLAnchorElement) return element
        if (element is HTMLElement && element.getAttribute("role") == "tab") return element
        element = element.parentElement
    }
    return null
}
